import { ToolDetail } from '@/components/report/ToolDetailDialog';

export const sampleReport = {
  reportId: 'GT-2026-0110-FD-001',
  reportDate: 'January 10, 2026',
  pluginName: 'feature-dev',
  classification: 'Confidential',
  generatedAt: new Date(),
  githubTokenPresent: true,

  scores: {
    aivss: { score: 5.4, level: 'medium' as const },
    cvss: { score: 4.2, level: 'medium' as const },
    aars: { score: 6.1, level: 'medium' as const },
    creatorTrust: { score: 9.2, level: 'low' as const },
    overall: 35, // cvss*10*0.4 + aivss*10*0.3 + aars*0.3 = 16.8 + 16.2 + 1.8 = 35
  },

  scoreBreakdowns: {
    aivss: [
      {
        name: 'Prompt Injection Susceptibility',
        value: 4.5,
        maxValue: 10,
        weight: 25,
        description: 'Measures resistance to prompt injection attacks',
      },
      {
        name: 'Data Exfiltration Risk',
        value: 6.2,
        maxValue: 10,
        weight: 20,
        description: 'Potential for unauthorized data extraction',
      },
      {
        name: 'Model Manipulation',
        value: 5.0,
        maxValue: 10,
        weight: 20,
        description: 'Risk of adversarial model manipulation',
      },
      {
        name: 'Output Integrity',
        value: 5.8,
        maxValue: 10,
        weight: 20,
        description: 'Reliability and consistency of outputs',
      },
      {
        name: 'AI Safety Controls',
        value: 5.5,
        maxValue: 10,
        weight: 15,
        description: 'Presence of safety guardrails',
      },
    ],
    cvss: [
      {
        name: 'Attack Vector',
        value: 3.5,
        maxValue: 10,
        weight: 20,
        description: 'Network-based attack surface',
      },
      {
        name: 'Attack Complexity',
        value: 4.0,
        maxValue: 10,
        weight: 15,
        description: 'Complexity required for exploitation',
      },
      {
        name: 'Privileges Required',
        value: 4.5,
        maxValue: 10,
        weight: 15,
        description: 'Level of access needed',
      },
      {
        name: 'User Interaction',
        value: 5.0,
        maxValue: 10,
        weight: 15,
        description: 'User involvement in attack',
      },
      {
        name: 'Confidentiality Impact',
        value: 4.2,
        maxValue: 10,
        weight: 15,
        description: 'Impact on data confidentiality',
      },
      {
        name: 'Integrity Impact',
        value: 3.8,
        maxValue: 10,
        weight: 10,
        description: 'Impact on data integrity',
      },
      {
        name: 'Availability Impact',
        value: 4.5,
        maxValue: 10,
        weight: 10,
        description: 'Impact on service availability',
      },
    ],
    aars: [
      {
        name: 'Decision Authority',
        value: 6.2,
        maxValue: 10,
        weight: 20,
        description: 'Scope of autonomous decision-making (higher = riskier)',
      },
      {
        name: 'Human Oversight',
        value: 3.8,
        maxValue: 10,
        weight: 20,
        description: 'Quality of human-in-the-loop controls (higher = safer)',
      },
      {
        name: 'Impact Scope',
        value: 5.5,
        maxValue: 10,
        weight: 15,
        description: 'Breadth of systems affected by agent actions (higher = riskier)',
      },
      {
        name: 'Reversibility',
        value: 3.2,
        maxValue: 10,
        weight: 15,
        description: 'Difficulty of undoing agent actions (higher = safer)',
      },
      {
        name: 'Time Pressure',
        value: 4.5,
        maxValue: 10,
        weight: 15,
        description: 'Speed at which decisions must be made (higher = riskier)',
      },
      {
        name: 'Transparency',
        value: 7.2,
        maxValue: 10,
        weight: 8,
        description: 'Observability of agent reasoning and actions (higher = safer)',
      },
      {
        name: 'Error Handling',
        value: 6.8,
        maxValue: 10,
        weight: 7,
        description: 'Quality of error detection and recovery (higher = safer)',
      },
    ],
  },

  mcpTools: {
    totalTools: 8,
    highRiskTools: 2,
    mediumRiskTools: 3,
    lowRiskTools: 3,
    toolsList: ['Read', 'Glob', 'Grep', 'WebFetch', 'WebSearch', 'Task', 'Agent', 'TodoRead'],
    toolsInfo: {
      Read: {
        name: 'Read',
        description:
          'Reads file contents from the filesystem with read-only access. Safe for codebase exploration.',
        riskLevel: 'low' as const,
        origin: 'claude-plugins-official/core-tools v1.2.0',
        capabilities: [
          'Read file contents',
          'Access text and binary files',
          'Read multiple files in sequence',
        ],
      },
      Glob: {
        name: 'Glob',
        description:
          'Searches for files matching specified patterns in the filesystem using glob patterns.',
        riskLevel: 'low' as const,
        origin: 'claude-plugins-official/core-tools v1.2.0',
        capabilities: [
          'Find files by pattern',
          'Recursive directory search',
          'Filter by file extension',
        ],
      },
      Grep: {
        name: 'Grep',
        description: 'Searches for text patterns within files using regular expressions.',
        riskLevel: 'low' as const,
        origin: 'claude-plugins-official/core-tools v1.2.0',
        capabilities: ['Search text patterns', 'Regular expression matching', 'Multi-file search'],
      },
      WebFetch: {
        name: 'WebFetch',
        description:
          'Makes HTTP requests to fetch content from external URLs. Potential data exfiltration risk.',
        riskLevel: 'high' as const,
        origin: 'claude-plugins-official/web-tools v2.0.1',
        capabilities: ['HTTP GET/POST requests', 'Download web content', 'API interactions'],
      },
      WebSearch: {
        name: 'WebSearch',
        description: 'Performs web searches to find information from search engines.',
        riskLevel: 'high' as const,
        origin: 'claude-plugins-official/web-tools v2.0.1',
        capabilities: ['Search engine queries', 'Find documentation', 'Research topics'],
      },
      Task: {
        name: 'Task',
        description: 'Creates and manages sub-tasks for workflow orchestration and delegation.',
        riskLevel: 'medium' as const,
        origin: 'claude-plugins-official/orchestration v1.5.0',
        capabilities: ['Create sub-tasks', 'Manage task dependencies', 'Coordinate workflows'],
      },
      Agent: {
        name: 'Agent',
        description:
          'Spawns autonomous sub-agents with delegated permissions for parallel execution.',
        riskLevel: 'medium' as const,
        origin: 'claude-plugins-official/orchestration v1.5.0',
        capabilities: ['Spawn sub-agents', 'Delegate permissions', 'Parallel execution'],
      },
      TodoRead: {
        name: 'TodoRead',
        description: 'Reads TODO items and task lists from the codebase for tracking work items.',
        riskLevel: 'low' as const,
        origin: 'claude-plugins-official/productivity v1.0.0',
        capabilities: ['Read TODO comments', 'Parse task files', 'Track work items'],
      },
    },
  },

  vulnerabilities: {
    critical: 0,
    high: 1,
    medium: 4,
    low: 3,
    info: 4,
  },

  keyFindings: [
    {
      finding: 'Verified Official Plugin',
      status: 'pass' as const,
      details: 'Authentic plugin by verified creator',
      linkedSection: 'creator-verification',
    },
    {
      finding: 'Read-Only Design',
      status: 'pass' as const,
      details: 'Cannot modify files (no Write/Edit/Bash)',
      linkedSection: 'permission-scope',
    },
    {
      finding: 'Network Exposure',
      status: 'warning' as const,
      details: 'WebFetch and WebSearch tools present',
      linkedSection: 'vulnerability-assessment',
    },
    {
      finding: 'Multi-Agent Architecture',
      status: 'info' as const,
      details: 'Spawns 6-9 agents per session',
      linkedSection: 'permission-scope',
    },
    {
      finding: 'Human Oversight',
      status: 'pass' as const,
      details: 'Multiple approval checkpoints in workflow',
      linkedSection: 'recommendation',
    },
  ],

  creator: {
    name: 'Alex Chen',
    organization: 'Acme AI Labs',
    verified: true,
    country: 'United States',
    organizationType: 'AI Development Company',
    trustScore: 9.2,
  },

  plugin: {
    name: 'feature-dev',
    description:
      'Comprehensive feature development workflow with specialized agents for codebase exploration, architecture design, and quality review',
    version: '1.0.0',
    location: '.claude/plugins/marketplaces/claude-plugins-official/plugins/feature-dev/',
    category: 'Development',
    license: 'MIT',
  },

  vulnerabilityDetails: [
    {
      id: 'VULN-001',
      title: 'Excessive Tool Permissions',
      severity: 'high' as const,
      description:
        'The plugin has access to WebFetch and WebSearch tools which could potentially be exploited for data exfiltration or accessing malicious content.',
      impact:
        'An attacker could potentially manipulate the agent to fetch malicious content or exfiltrate sensitive data through web requests.',
      remediation:
        'Implement strict URL allowlisting and rate limiting for web access tools. Add content filtering for fetched data.',
      cwe: 'CWE-269',
    },
    {
      id: 'VULN-002',
      title: 'Input Sanitization Gap',
      severity: 'medium' as const,
      description:
        'User inputs passed to spawned agents are not consistently sanitized, potentially allowing prompt injection.',
      impact: 'Malicious prompts could influence agent behavior in unintended ways.',
      remediation:
        'Implement input validation and sanitization before passing user content to sub-agents.',
      cwe: 'CWE-20',
    },
    {
      id: 'VULN-003',
      title: 'Scope Boundary Weakness',
      severity: 'medium' as const,
      description:
        'Agent scope boundaries could potentially be bypassed through carefully crafted instructions.',
      impact: 'Agents might access files or perform actions outside their intended scope.',
      remediation: 'Strengthen scope enforcement with explicit deny rules and audit logging.',
    },
    {
      id: 'VULN-004',
      title: 'Rate Limiting Absent',
      severity: 'low' as const,
      description:
        'No rate limiting on agent spawning or tool invocations could lead to resource exhaustion.',
      impact: 'Denial of service through resource exhaustion.',
      remediation: 'Implement rate limits on agent spawning and tool calls per session.',
    },
    {
      id: 'VULN-005',
      title: 'Audit Logging Incomplete',
      severity: 'medium' as const,
      description: 'Some agent actions are not fully logged for security auditing purposes.',
      impact: 'Difficulty in forensic analysis if security incident occurs.',
      remediation:
        'Implement comprehensive audit logging for all agent actions and tool invocations.',
    },
    {
      id: 'VULN-006',
      title: 'Session Timeout Not Configured',
      severity: 'medium' as const,
      description: 'Agent sessions do not have configured timeout limits.',
      impact: 'Long-running sessions could accumulate resource usage.',
      remediation:
        'Configure appropriate session timeout limits based on expected workflow duration.',
    },
    {
      id: 'VULN-007',
      title: 'Documentation Gap',
      severity: 'low' as const,
      description: 'Security documentation for the plugin is incomplete.',
      impact: 'Users may not be aware of all security considerations.',
      remediation: 'Complete security documentation with clear guidelines.',
    },
    {
      id: 'VULN-008',
      title: 'Version Pinning Missing',
      severity: 'low' as const,
      description: 'Dependencies are not version-pinned which could lead to unexpected behavior.',
      impact: 'Dependency updates could introduce security vulnerabilities.',
      remediation: 'Pin all dependencies to specific versions.',
    },
    {
      id: 'VULN-009',
      title: 'Cache Configuration',
      severity: 'info' as const,
      description: 'Cache settings are using default values.',
      impact: 'Performance could be optimized with custom cache settings.',
      remediation: 'Review and optimize cache configuration.',
    },
    {
      id: 'VULN-010',
      title: 'Telemetry Enabled',
      severity: 'info' as const,
      description: 'Default telemetry is enabled for usage tracking.',
      impact: 'Usage data is shared with the provider.',
      remediation: 'Review telemetry settings based on privacy requirements.',
    },
    {
      id: 'VULN-011',
      title: 'Debug Mode Check',
      severity: 'info' as const,
      description: 'Debug mode detection is recommended for production deployments.',
      impact: 'Debug information could leak in production.',
      remediation: 'Ensure debug mode is disabled in production.',
    },
    {
      id: 'VULN-012',
      title: 'Configuration Review',
      severity: 'info' as const,
      description: 'Configuration files should be reviewed for best practices.',
      impact: 'Suboptimal configuration could affect performance.',
      remediation: 'Review configuration against recommended best practices.',
    },
  ],

  permissions: [
    {
      capability: 'Code Execution',
      riskLevel: 'low' as const,
      tools: ['Read', 'Glob', 'Grep'],
      icon: 'code' as const,
      riskJustification:
        'Read-only access to files. Cannot execute arbitrary code or modify the filesystem. Limited to pattern matching and file reading operations.',
    },
    {
      capability: 'Network Access',
      riskLevel: 'high' as const,
      tools: ['WebFetch', 'WebSearch'],
      icon: 'network' as const,
      riskJustification:
        'Can make outbound HTTP requests and search the web. Potential for data exfiltration or fetching malicious content. Requires URL allowlisting and content filtering.',
    },
    {
      capability: 'File System',
      riskLevel: 'low' as const,
      tools: ['Read (read-only)'],
      icon: 'file' as const,
      riskJustification:
        'Limited to read-only operations. Cannot create, modify, or delete files. Safe for codebase exploration and analysis.',
    },
    {
      capability: 'Agent Spawning',
      riskLevel: 'medium' as const,
      tools: ['Task', 'Agent'],
      icon: 'terminal' as const,
      riskJustification:
        'Can spawn sub-agents with delegated permissions. Increases attack surface but bounded by parent permissions. Requires monitoring of agent hierarchies.',
    },
  ],

  recommendation: {
    type: 'approved-monitoring' as const,
    summary:
      'This plugin demonstrates good security practices with read-only file access and human oversight checkpoints. However, network access capabilities require monitoring.',
    conditions: [
      'Enable audit logging for all network requests',
      'Implement URL allowlisting for WebFetch operations',
      'Monitor agent spawning patterns for anomalies',
      'Review session logs weekly for the first month',
    ],
  },

  threatModeling: {
    threatActors: [
      {
        name: 'Malicious User',
        motivation: 'Data exfiltration or system compromise',
        capability: 'medium' as const,
        likelihood: 'medium' as const,
      },
      {
        name: 'Compromised Dependency',
        motivation: 'Supply chain attack',
        capability: 'high' as const,
        likelihood: 'low' as const,
      },
      {
        name: 'Prompt Injection Attacker',
        motivation: 'Manipulate agent behavior',
        capability: 'medium' as const,
        likelihood: 'high' as const,
      },
      {
        name: 'Insider Threat',
        motivation: 'Unauthorized data access',
        capability: 'high' as const,
        likelihood: 'low' as const,
      },
    ],
    attackVectors: [
      {
        name: 'Prompt Injection via User Input',
        description:
          'Attacker crafts malicious prompts to manipulate agent behavior and bypass security controls.',
        riskLevel: 'high' as const,
        mitigations: [
          'Implement input sanitization and validation',
          'Use structured prompts with clear boundaries',
          'Add output filtering and verification',
        ],
      },
      {
        name: 'Data Exfiltration via WebFetch',
        description:
          'Malicious actor uses network tools to send sensitive data to external servers.',
        riskLevel: 'high' as const,
        mitigations: [
          'Implement strict URL allowlisting',
          'Monitor and log all network requests',
          'Add content inspection for outbound data',
        ],
      },
      {
        name: 'Scope Boundary Bypass',
        description: 'Attacker attempts to access files or resources outside the defined scope.',
        riskLevel: 'medium' as const,
        mitigations: [
          'Enforce explicit deny rules',
          'Implement path canonicalization',
          'Add audit logging for boundary violations',
        ],
      },
    ],
    assetsCritical: [
      'Source Code Files',
      'Configuration Secrets',
      'API Credentials',
      'User Session Data',
      'Database Connections',
    ],
  },

  remediationItems: [
    {
      id: 'REM-001',
      title: 'Implement URL Allowlisting for WebFetch',
      description:
        'Configure strict URL allowlisting to prevent unauthorized network access and data exfiltration.',
      riskLevel: 'high' as const,
      priority: 'immediate' as const,
      effort: 'medium' as const,
      estimatedTime: '2-4 hours',
      relatedVulnerability: 'VULN-001',
      steps: [
        'Define list of approved domains and endpoints',
        'Implement URL validation before each fetch request',
        'Add logging for blocked requests',
        'Test with various URL patterns including edge cases',
      ],
    },
    {
      id: 'REM-002',
      title: 'Add Input Sanitization Layer',
      description:
        'Implement comprehensive input validation and sanitization to prevent prompt injection attacks.',
      riskLevel: 'medium' as const,
      priority: 'short-term' as const,
      effort: 'high' as const,
      estimatedTime: '1-2 days',
      relatedVulnerability: 'VULN-002',
      steps: [
        'Identify all user input entry points',
        'Implement input validation rules',
        'Add escape sequences for special characters',
        'Create unit tests for sanitization logic',
      ],
    },
    {
      id: 'REM-003',
      title: 'Implement Rate Limiting',
      description:
        'Add rate limiting to prevent resource exhaustion from excessive agent spawning or tool calls.',
      riskLevel: 'low' as const,
      priority: 'medium-term' as const,
      effort: 'low' as const,
      estimatedTime: '2-3 hours',
      relatedVulnerability: 'VULN-004',
      steps: [
        'Define rate limits for agent spawning',
        'Implement token bucket or sliding window algorithm',
        'Add monitoring for rate limit violations',
      ],
    },
    {
      id: 'REM-004',
      title: 'Complete Security Documentation',
      description:
        'Document all security considerations, best practices, and incident response procedures.',
      riskLevel: 'low' as const,
      priority: 'long-term' as const,
      effort: 'medium' as const,
      estimatedTime: '1 week',
      relatedVulnerability: 'VULN-007',
      steps: [
        'Document security architecture',
        'Create threat model documentation',
        'Write incident response playbook',
        'Add security guidelines for developers',
      ],
    },
  ],

  // Canonical name used by InsightsReport — mirrors remediationItems above
  prioritizedRemediation: [] as Array<{
    id: string;
    title: string;
    description: string;
    riskLevel: 'critical' | 'high' | 'medium' | 'low' | 'info';
    priority: 'immediate' | 'short-term' | 'medium-term' | 'long-term';
    effort: 'low' | 'medium' | 'high';
    estimatedTime: string;
    relatedVulnerability?: string;
    steps: string[];
  }>,

  riskAcceptanceItems: [
    {
      id: 'RISK-001',
      risk: 'Read-only file system access enables codebase exploration',
      riskLevel: 'low' as const,
      status: 'accepted' as const,
      rationale:
        'Read-only access is required for core functionality and poses minimal risk without write capabilities.',
      owner: 'Security Team',
      reviewDate: '2026-04-10',
    },
    {
      id: 'RISK-002',
      risk: 'Network access via WebFetch and WebSearch tools',
      riskLevel: 'high' as const,
      status: 'mitigated' as const,
      rationale:
        'Essential for fetching documentation and resources. Risk mitigated through URL allowlisting and monitoring.',
      owner: 'Platform Team',
      reviewDate: '2026-02-10',
      residualRisk: 'medium' as const,
    },
    {
      id: 'RISK-003',
      risk: 'Multi-agent spawning increases attack surface',
      riskLevel: 'medium' as const,
      status: 'mitigated' as const,
      rationale:
        'Required for workflow orchestration. Mitigated through permission inheritance limits and monitoring.',
      owner: 'Architecture Team',
      reviewDate: '2026-03-10',
      residualRisk: 'low' as const,
    },
    {
      id: 'RISK-004',
      risk: 'Third-party plugin dependency risks',
      riskLevel: 'medium' as const,
      status: 'transferred' as const,
      rationale:
        'Risk transferred to plugin vendor through SLA agreement requiring security updates within 48 hours.',
      owner: 'Vendor Management',
      reviewDate: '2026-06-10',
    },
  ],

  // AI Bill of Materials (AI-BOM)
  aiBom: {
    llmComponents: [
      {
        name: 'Claude Sonnet 4.6',
        version: 'claude-sonnet-4-6',
        provider: 'Anthropic',
        purpose: 'Primary reasoning and code analysis',
      },
      {
        name: 'Claude Haiku 4.5',
        version: 'claude-haiku-4-5-20251001',
        provider: 'Anthropic',
        purpose: 'Fast sub-agent operations',
      },
    ],
    apiComponents: [
      {
        name: 'GitHub API',
        version: 'v3',
        endpoint: 'api.github.com',
        authentication: 'OAuth 2.0',
      },
      {
        name: 'NPM Registry',
        version: 'v1',
        endpoint: 'registry.npmjs.org',
        authentication: 'Bearer Token',
      },
      {
        name: 'Snyk Security API',
        version: 'v1',
        endpoint: 'api.snyk.io',
        authentication: 'API Key',
      },
    ],
    libraryComponents: [
      {
        name: '@anthropic-ai/sdk',
        version: '0.25.0',
        license: 'MIT',
        purpose: 'Claude API integration',
      },
      { name: 'zod', version: '3.22.4', license: 'MIT', purpose: 'Schema validation' },
      { name: 'axios', version: '1.6.2', license: 'MIT', purpose: 'HTTP client' },
      { name: 'winston', version: '3.11.0', license: 'MIT', purpose: 'Logging framework' },
      { name: 'lodash', version: '4.17.21', license: 'MIT', purpose: 'Utility functions' },
    ],
    systemTools: [
      {
        name: 'bash',
        accessLevel: 'execute' as const,
        scope: 'Sandboxed environment',
        justification: 'Required for build commands',
      },
      {
        name: 'filesystem',
        accessLevel: 'read' as const,
        scope: 'Project directory only',
        justification: 'Codebase exploration',
      },
      {
        name: 'network',
        accessLevel: 'write' as const,
        scope: 'Allowlisted domains',
        justification: 'API calls and documentation fetching',
      },
    ],
  },

  // Heatmap data
  heatmapData: {
    aivss: [
      {
        label: 'Prompt Injection',
        likelihood: 'moderately-high' as const,
        impact: 'high' as const,
        description:
          'Measures resistance to prompt injection attacks that could manipulate agent behavior.',
        recommendation: 'Implement input validation and structured prompts with clear boundaries.',
        linkedSection: 'vulnerability-assessment',
      },
      {
        label: 'Data Exfiltration',
        likelihood: 'moderate' as const,
        impact: 'high' as const,
        description: 'Potential for unauthorized data extraction through network tools.',
        recommendation: 'Enable URL allowlisting and content filtering for all network requests.',
        linkedSection: 'vulnerability-assessment',
      },
      {
        label: 'Model Manipulation',
        likelihood: 'moderate' as const,
        impact: 'moderately-high' as const,
        description: 'Risk of adversarial manipulation of model outputs.',
        recommendation: 'Add output verification and consistency checks.',
        linkedSection: 'scoring-breakdown',
      },
      {
        label: 'Output Integrity',
        likelihood: 'moderately-high' as const,
        impact: 'moderate' as const,
        description: 'Reliability and consistency of agent outputs.',
        recommendation: 'Implement output validation and monitoring.',
        linkedSection: 'scoring-breakdown',
      },
      {
        label: 'AI Safety Controls',
        likelihood: 'low' as const,
        impact: 'moderate' as const,
        description: 'Presence and effectiveness of safety guardrails.',
        recommendation: 'Review and strengthen existing safety mechanisms.',
        linkedSection: 'scoring-breakdown',
      },
    ],
    cvss: [
      {
        label: 'Attack Vector',
        likelihood: 'moderate' as const,
        impact: 'moderate' as const,
        description: 'Network-based attack surface exposure.',
        recommendation: 'Minimize exposed endpoints and implement network segmentation.',
        linkedSection: 'threat-modeling',
      },
      {
        label: 'Attack Complexity',
        likelihood: 'low' as const,
        impact: 'moderate' as const,
        description: 'Level of difficulty required for successful exploitation.',
        recommendation: 'Add authentication layers and complexity barriers.',
        linkedSection: 'threat-modeling',
      },
      {
        label: 'Privileges Required',
        likelihood: 'moderate' as const,
        impact: 'moderately-high' as const,
        description: 'Level of access needed to exploit vulnerabilities.',
        recommendation: 'Implement least-privilege access controls.',
        linkedSection: 'permission-scope',
      },
      {
        label: 'User Interaction',
        likelihood: 'moderately-high' as const,
        impact: 'moderate' as const,
        description: 'Degree of user involvement required in potential attacks.',
        recommendation: 'Add user confirmation for sensitive operations.',
        linkedSection: 'permission-scope',
      },
      {
        label: 'Confidentiality',
        likelihood: 'moderate' as const,
        impact: 'high' as const,
        description: 'Impact on data confidentiality if exploited.',
        recommendation: 'Encrypt sensitive data and limit access scope.',
        linkedSection: 'vulnerability-assessment',
      },
      {
        label: 'Integrity Impact',
        likelihood: 'low' as const,
        impact: 'moderately-high' as const,
        description: 'Potential for unauthorized data modification.',
        recommendation: 'Implement integrity checks and audit logging.',
        linkedSection: 'vulnerability-assessment',
      },
    ],
    aars: [
      {
        label: 'Decision Authority',
        likelihood: 'high' as const,
        impact: 'high' as const,
        description: 'Scope of autonomous decision-making by the agent.',
        recommendation: 'Limit autonomous actions to low-impact operations.',
        linkedSection: 'threat-modeling',
      },
      {
        label: 'Human Oversight',
        likelihood: 'low' as const,
        impact: 'moderate' as const,
        description: 'Quality of human-in-the-loop controls and approval checkpoints.',
        recommendation: 'Maintain current oversight mechanisms.',
        linkedSection: 'recommendation',
      },
      {
        label: 'Impact Scope',
        likelihood: 'moderately-high' as const,
        impact: 'moderately-high' as const,
        description: 'Breadth of systems affected by agent actions.',
        recommendation: 'Implement explicit deny rules and boundary monitoring.',
        linkedSection: 'permission-scope',
      },
      {
        label: 'Reversibility',
        likelihood: 'moderate' as const,
        impact: 'moderately-high' as const,
        description: 'Ability to undo agent actions if needed.',
        recommendation: 'Implement action logging and rollback capabilities.',
        linkedSection: 'prioritized-remediation',
      },
      {
        label: 'Time Pressure',
        likelihood: 'moderate' as const,
        impact: 'moderate' as const,
        description: 'Speed at which decisions must be made.',
        recommendation: 'Avoid real-time autonomous decision-making where possible.',
        linkedSection: 'threat-modeling',
      },
      {
        label: 'Transparency',
        likelihood: 'low' as const,
        impact: 'moderate' as const,
        description: 'Observability of agent reasoning and decision processes.',
        recommendation: 'Maintain detailed logging and reasoning traces.',
        linkedSection: 'scoring-breakdown',
      },
      {
        label: 'Error Handling',
        likelihood: 'low' as const,
        impact: 'moderately-high' as const,
        description: 'Quality of error detection and recovery mechanisms.',
        recommendation: 'Add circuit breakers and graceful degradation.',
        linkedSection: 'scoring-breakdown',
      },
    ],
    vulnerability: [
      {
        label: 'Tool Permissions',
        likelihood: 'high' as const,
        impact: 'high' as const,
        description: 'WebFetch and WebSearch tools could be exploited for data exfiltration.',
        recommendation: 'Implement strict URL allowlisting and rate limiting.',
        linkedSection: 'vulnerability-assessment',
        linkedVulnerabilityId: 'VULN-001',
      },
      {
        label: 'Input Sanitization',
        likelihood: 'moderately-high' as const,
        impact: 'moderately-high' as const,
        description: 'User inputs to agents lack consistent sanitization.',
        recommendation: 'Add comprehensive input validation layer.',
        linkedSection: 'vulnerability-assessment',
        linkedVulnerabilityId: 'VULN-002',
      },
      {
        label: 'Scope Boundary',
        likelihood: 'moderate' as const,
        impact: 'moderately-high' as const,
        description: 'Agent scope boundaries could potentially be bypassed.',
        recommendation: 'Strengthen scope enforcement with explicit deny rules.',
        linkedSection: 'vulnerability-assessment',
        linkedVulnerabilityId: 'VULN-003',
      },
      {
        label: 'Rate Limiting',
        likelihood: 'low' as const,
        impact: 'low' as const,
        description: 'No rate limiting on agent spawning or tool invocations.',
        recommendation: 'Implement rate limits per session.',
        linkedSection: 'vulnerability-assessment',
        linkedVulnerabilityId: 'VULN-004',
      },
      {
        label: 'Audit Logging',
        likelihood: 'moderate' as const,
        impact: 'moderate' as const,
        description: 'Some agent actions are not fully logged.',
        recommendation: 'Implement comprehensive audit logging.',
        linkedSection: 'vulnerability-assessment',
        linkedVulnerabilityId: 'VULN-005',
      },
      {
        label: 'Session Timeout',
        likelihood: 'low' as const,
        impact: 'moderate' as const,
        description: 'Agent sessions lack configured timeout limits.',
        recommendation: 'Configure appropriate session timeouts.',
        linkedSection: 'vulnerability-assessment',
        linkedVulnerabilityId: 'VULN-006',
      },
    ],
  },
};

// Alias: InsightsReport uses `prioritizedRemediation`, components use `remediationItems`
sampleReport.prioritizedRemediation = sampleReport.remediationItems;

// Tool details for clickable tools
export const toolDetails: Record<string, ToolDetail> = {
  Read: {
    name: 'Read',
    description: 'Reads file contents from the filesystem with read-only access.',
    riskLevel: 'low',
    riskReason:
      'Read-only access cannot modify data. However, could potentially read sensitive files if not properly scoped.',
    capabilities: [
      'Read file contents',
      'Access text and binary files',
      'Read multiple files in sequence',
    ],
  },
  Glob: {
    name: 'Glob',
    description: 'Searches for files matching specified patterns in the filesystem.',
    riskLevel: 'low',
    riskReason:
      'Pattern matching is read-only. Could potentially enumerate sensitive file paths if not scoped.',
    capabilities: [
      'Find files by pattern',
      'Recursive directory search',
      'Filter by file extension',
    ],
  },
  Grep: {
    name: 'Grep',
    description: 'Searches for text patterns within files.',
    riskLevel: 'low',
    riskReason:
      'Text search is read-only. Could find sensitive information but cannot exfiltrate without other tools.',
    capabilities: ['Search text patterns', 'Regular expression matching', 'Multi-file search'],
  },
  WebFetch: {
    name: 'WebFetch',
    description: 'Makes HTTP requests to fetch content from the web.',
    riskLevel: 'high',
    riskReason:
      'Can exfiltrate data through HTTP requests and fetch potentially malicious content. Requires strict URL allowlisting.',
    capabilities: ['HTTP GET/POST requests', 'Download web content', 'API interactions'],
  },
  WebSearch: {
    name: 'WebSearch',
    description: 'Performs web searches to find information.',
    riskLevel: 'high',
    riskReason:
      'Can search for sensitive topics and access external information. Results could be manipulated by SEO attacks.',
    capabilities: ['Search engine queries', 'Find documentation', 'Research topics'],
  },
  Task: {
    name: 'Task',
    description: 'Creates and manages sub-tasks for workflow orchestration.',
    riskLevel: 'medium',
    riskReason:
      'Can delegate work to sub-agents, increasing attack surface. Bounded by parent permissions but adds complexity.',
    capabilities: ['Create sub-tasks', 'Manage task dependencies', 'Coordinate workflows'],
  },
  Agent: {
    name: 'Agent',
    description: 'Spawns autonomous sub-agents with delegated permissions.',
    riskLevel: 'medium',
    riskReason:
      'Sub-agents operate with delegated autonomy. Could amplify vulnerabilities if not properly constrained.',
    capabilities: ['Spawn sub-agents', 'Delegate permissions', 'Parallel execution'],
  },
  TodoRead: {
    name: 'TodoRead',
    description: 'Reads TODO items and task lists from the codebase.',
    riskLevel: 'low',
    riskReason: 'Read-only access to task metadata. Minimal security impact.',
    capabilities: ['Read TODO comments', 'Parse task files', 'Track work items'],
  },
};

// ============================================
// DYNAMIC DATA LOADING (added by setup script)
// ============================================
// Window augmentation for GUARDRAIL_REPORT_DATA is declared in useReportData.ts
if (typeof window !== 'undefined' && window.GUARDRAIL_REPORT_DATA) {
  const dynamicData = window.GUARDRAIL_REPORT_DATA;

  // Update report metadata
  if (dynamicData.reportId) sampleReport.reportId = dynamicData.reportId as string;
  if (dynamicData.reportDate) sampleReport.reportDate = dynamicData.reportDate as string;
  if (dynamicData.pluginName) sampleReport.pluginName = dynamicData.pluginName as string;
  if (dynamicData.classification)
    sampleReport.classification = dynamicData.classification as string;

  // Update scores
  if (dynamicData.scores) {
    const scores = dynamicData.scores as Record<string, unknown>;
    if (scores.aivss) Object.assign(sampleReport.scores.aivss, scores.aivss);
    if (scores.cvss) Object.assign(sampleReport.scores.cvss, scores.cvss);
    if (scores.aars) Object.assign(sampleReport.scores.aars, scores.aars);
    if (scores.creatorTrust)
      Object.assign(sampleReport.scores.creatorTrust, scores.creatorTrust);
    if (scores.overall != null) sampleReport.scores.overall = scores.overall as number;
  }

  // Update creator info
  if (dynamicData.creator) Object.assign(sampleReport.creator, dynamicData.creator);

  // Update plugin info
  if (dynamicData.plugin) Object.assign(sampleReport.plugin, dynamicData.plugin);

  // Update vulnerability counts
  if (dynamicData.vulnerabilities)
    Object.assign(sampleReport.vulnerabilities, dynamicData.vulnerabilities);

  // Update recommendation
  if (dynamicData.recommendation)
    Object.assign(sampleReport.recommendation, dynamicData.recommendation);

  // DETAILED SECTIONS - Replace if provided, clear if not
  // This prevents sample data from showing for sections not in the scan

  // Key Findings
  sampleReport.keyFindings = (dynamicData.keyFindings as typeof sampleReport.keyFindings) ?? [];

  // Vulnerability Details
  sampleReport.vulnerabilityDetails =
    (dynamicData.vulnerabilityDetails as typeof sampleReport.vulnerabilityDetails) ?? [];

  // Score Breakdowns
  sampleReport.scoreBreakdowns =
    (dynamicData.scoreBreakdowns as typeof sampleReport.scoreBreakdowns) ?? {
      aivss: [],
      cvss: [],
      aars: [],
    };

  // MCP Tools
  sampleReport.mcpTools = (dynamicData.mcpTools as typeof sampleReport.mcpTools) ?? {
    totalTools: 0,
    highRiskTools: 0,
    mediumRiskTools: 0,
    lowRiskTools: 0,
    toolsList: [],
    toolsInfo: {},
  };

  // Permissions
  sampleReport.permissions =
    (dynamicData.permissions as typeof sampleReport.permissions) ?? [];

  // Threat Modeling
  sampleReport.threatModeling =
    (dynamicData.threatModeling as typeof sampleReport.threatModeling) ?? {
      threatActors: [],
      attackVectors: [],
      assetsCritical: [],
    };

  // Remediation Items
  sampleReport.remediationItems =
    (dynamicData.remediationItems as typeof sampleReport.remediationItems) ?? [];

  // Risk Acceptance Items
  sampleReport.riskAcceptanceItems =
    (dynamicData.riskAcceptanceItems as typeof sampleReport.riskAcceptanceItems) ?? [];

  // Heatmap Data
  sampleReport.heatmapData =
    (dynamicData.heatmapData as typeof sampleReport.heatmapData) ?? {
      aivss: [],
      cvss: [],
      aars: [],
      vulnerability: [],
    };

  // AI Bill of Materials
  sampleReport.aiBom = (dynamicData.aiBom as typeof sampleReport.aiBom) ?? {
    llmComponents: [],
    apiComponents: [],
    libraryComponents: [],
    systemTools: [],
  };
}
// ============================================
